import {
  getHeightUnits,
  getWeightUnits,
  hasDateOfBirthBookingQuestion,
  hasHeightQuestion,
  hasPassportExpiryQuestion,
  hasPassportNationalityQuestion,
  hasPassportNumberQuestion,
  hasWeightQuestion,
} from '.'
import {
  Basket,
  ExperienceBasketItem,
  FlightBasketItem,
  HotelBasketItem,
  isFlightBasketItem,
} from '../basket'
import { AgeGroup, AgeGroupSchema } from '../common'

type BaseFlightHotelTraveler = {
  id: string
  ageGroup: AgeGroup
  age?: number
  requireTitle: boolean
  requireName: boolean
  requireDateOfBirth: boolean
  requireGender: boolean
}

type FlightTraveler = BaseFlightHotelTraveler & {
  type: 'flight'
  departureDateTime: string
}

type HotelTraveler = BaseFlightHotelTraveler & {
  type: 'hotel'
}

export type FlightHotelTraveler = FlightTraveler | HotelTraveler

export type ExperienceTraveler = {
  id: string
  type: 'experience'
  ageGroup: AgeGroup
  startAge?: number
  endAge?: number
  requireDateOfBirth: boolean
  requireHeight: boolean
  requireWeight: boolean
  requirePassportNumber: boolean
  requirePassportNationality: boolean
  requirePassportExpiry: boolean
  heightUnits: string[]
  weightUnits: string[]
}

type FlightOrHotelBasketItem = FlightBasketItem | HotelBasketItem

const getFlightHotelTravelers = (basketItem: FlightOrHotelBasketItem): FlightHotelTraveler[] => {
  if (isFlightBasketItem(basketItem)) {
    const { departureDateTime } = basketItem.outboundFlightJourney.segments[0]

    return basketItem.travelers.map((traveler) => ({
      ...traveler,
      type: 'flight',
      requireName: true,
      requireDateOfBirth:
        Boolean(basketItem.isSecure) || (traveler.age != null && traveler.age <= 16),
      requireGender: Boolean(basketItem.isSecure),
      requireTitle: true,
      departureDateTime,
    }))
  }

  return basketItem.travelers.flatMap((group) => {
    const firstAdultInRoomIndex = group.findIndex(
      (traveler) => traveler.ageGroup === AgeGroupSchema.enum.ADULT
    )
    return group.map((traveler, index) => ({
      ...traveler,
      type: 'hotel',
      requireName: firstAdultInRoomIndex === index,
      requireDateOfBirth: false,
      requireGender: false,
      requireTitle: false,
    }))
  })
}

type SortableTraveler = { ageGroup: AgeGroup }

const sortTravelersByAgeGroup = <T extends SortableTraveler>(travelers: T[]): T[] => {
  const sortOrder: AgeGroup[] = [
    AgeGroupSchema.enum.ADULT,
    AgeGroupSchema.enum.SENIOR,
    AgeGroupSchema.enum.YOUTH,
    AgeGroupSchema.enum.TRAVELER,
  ]
  return travelers.sort(
    ({ ageGroup: ageGroupA }, { ageGroup: ageGroupB }) =>
      sortOrder.indexOf(ageGroupA) - sortOrder.indexOf(ageGroupB)
  )
}

const getFlightHotelTravelerContext = (
  basketItem: FlightOrHotelBasketItem
): FlightHotelTravelerContext => {
  const Travelers = sortTravelersByAgeGroup(getFlightHotelTravelers(basketItem))
  const mainTraveler = Travelers.find(
    (traveler) => traveler.ageGroup === AgeGroupSchema.enum.ADULT && traveler.age === undefined
  )
  if (!mainTraveler) {
    throw Error('Could not find main traveler')
  }

  const typeIndicator = isFlightBasketItem(basketItem) ? 'flight' : 'hotel'

  const additionalTravelers = Travelers.filter((traveler) => traveler.id !== mainTraveler.id)
  return {
    type: typeIndicator,
    mainTraveler: {
      ...mainTraveler,
      requireName: true,
    },
    additionalTravelers,
  }
}

const getExperienceTravelers = (basketItem: ExperienceBasketItem): ExperienceTraveler[] => {
  return basketItem.travelers
    .map(({ ageGroup, startAge, endAge, quantity }, i) =>
      Array.from({ length: quantity }).map(
        (_, j): ExperienceTraveler => ({
          id: `${i}-${j}`,
          type: 'experience',
          ageGroup,
          startAge,
          endAge,
          requireDateOfBirth: hasDateOfBirthBookingQuestion(basketItem),
          requireHeight: hasHeightQuestion(basketItem),
          heightUnits: getHeightUnits(basketItem),
          requireWeight: hasWeightQuestion(basketItem),
          weightUnits: getWeightUnits(basketItem),
          requirePassportNumber: hasPassportNumberQuestion(basketItem),
          requirePassportNationality: hasPassportNationalityQuestion(basketItem),
          requirePassportExpiry: hasPassportExpiryQuestion(basketItem),
        })
      )
    )
    .flat()
}

const getExperienceTravelersContext = (
  basketItem: ExperienceBasketItem
): ExperienceTravelerContext => {
  const travelers = sortTravelersByAgeGroup(getExperienceTravelers(basketItem))
  const mainTraveler = travelers.find((traveler) => {
    const mainTravelerAgeGroups: AgeGroup[] = [
      AgeGroupSchema.enum.ADULT,
      AgeGroupSchema.enum.SENIOR,
      AgeGroupSchema.enum.YOUTH,
      AgeGroupSchema.enum.TRAVELER,
    ]

    return mainTravelerAgeGroups.includes(traveler.ageGroup)
  })

  if (!mainTraveler) {
    throw Error('Could not find main traveler')
  }

  const additionalTravelers = travelers.filter(({ id }) => id !== mainTraveler.id) ?? []

  return {
    type: 'experience',
    mainTraveler,
    additionalTravelers,
    startDate: basketItem.startDate.slice(0, 10),
  }
}

export type FlightHotelTravelerContext = {
  type: 'flight' | 'hotel'
  mainTraveler: FlightHotelTraveler
  additionalTravelers: FlightHotelTraveler[]
}

export type ExperienceTravelerContext = {
  type: 'experience'
  mainTraveler: ExperienceTraveler
  additionalTravelers: ExperienceTraveler[]
  startDate: string
}

export type TravelerContext = FlightHotelTravelerContext | ExperienceTravelerContext

export const getTravelerContext = (basket: Basket): TravelerContext => {
  const flightOrHotelItem = basket.items.FLIGHT?.at(0) || basket.items.HOTEL?.at(0)
  if (flightOrHotelItem) {
    return getFlightHotelTravelerContext(flightOrHotelItem)
  }

  const experienceItem = basket.items.EXPERIENCE?.at(0)
  if (experienceItem) {
    return getExperienceTravelersContext(experienceItem)
  }
  throw Error('Could not find traveler context')
}

export const safeGetTravelerContext = (basket: Basket): TravelerContext | undefined => {
  try {
    return getTravelerContext(basket)
  } catch (error) {
    return undefined
  }
}
